import React, { useState, useEffect, useRef } from "react";
import {
  Input,
  Select,
  Badge,
  Dropdown,
  Menu,
  Modal,
  Form,
  Button,
  message,
} from "antd";
import {
  MoreOutlined,
  SendOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  MessageOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { socket } from "../../Config/socket.io";
import consts from "../../constant";
import Loadingpage from "../Loadingpage/Loadingpage";

const Supports = () => {

  const { Search, TextArea } = Input;

  const token = window.localStorage.getItem("Jetix");
  const [queryId, setQueryId] = useState(null);
  const [chats, setChats] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const [messages, setMessages] = useState([]);
  const [title, setTitle] = useState("");
  const [searchText, setSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [form] = Form.useForm();
  const messagesEndRef = useRef(null);

  const [chatStatus, setChatStatus] = useState("opened");
  const queryIdRef = useRef(queryId);

  useEffect(() => {
    queryIdRef.current = queryId;
  }, [queryId]);

  // Fetch Queries (with pagination and server-side search)
  const fetchQueries = async (status, search = "", p = 1, append = false) => {
    if (loadingMore) return;
    if (p > 1) setLoadingMore(true);

    try {
      const response = await axios.post(`${consts.BackendUrl}/support/getallquery`, {
        status: status === "opened" ? false : status === "closed" ? true : undefined,
        search: search,
        page: p,
        limit: 15
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response?.data?.success) {
        const newChats = (response.data.result || []).map(chat => ({
          ...chat,
          isDisabled: chat.solved // Ensure isDisabled matches backend solved status
        }));
        if (append) {
          setChats(prev => [...prev, ...newChats]);
        } else {
          setChats(newChats);
        }
        setHasMore(response.data.hasMore);
        setPage(response.data.page);
      } else {
        if (!append) setChats([]);
        setHasMore(false);
      }
    } catch (err) {
      if (!append) setChats([]);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };


  useEffect(() => {
    fetchQueries(chatStatus, "", 1, false);
  }, [chatStatus]);


  useEffect(() => {
    if (socket.connected && queryId) {
      const selected = chats.find(c => c._id === queryId);
      if (selected?._id) {
        socket.emit("join_support_room", {
          queryid: selected._id,
          adminId: selected.admin_id
        });

        return () => {
          socket.emit("leave_support_room", {
            queryid: selected._id,
            adminId: selected.admin_id
          });
        };
      }
    }
  }, [socket.connected, queryId]);

  // Scroll listener for pagination
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // If user is near bottom (within 50px) and there's more to load
    if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !loadingMore) {
      fetchQueries(chatStatus, searchText, page + 1, true);
    }
  };

  useEffect(() => {
    if (!socket) return;

    const handleReceiveRaisedTicket = (data) => {
      if (data && data._id) {
        setChats((prevChats) => {
          const exists = prevChats.find((c) => c._id === data._id);
          if (exists) {
            return prevChats.map((c) => {
              if (c._id === data._id) {
                const isCurrent = c._id === queryIdRef.current;
                return {
                  ...c,
                  ...data,
                  // Preserve existing user_id info if new data only has an ID string
                  user_id: typeof data.user_id === 'object' ? data.user_id : c.user_id,
                  unreadCount: isCurrent ? 0 : (data.unreadCount !== undefined ? data.unreadCount : c.unreadCount)
                };
              }
              return c;
            }).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
          }
          return [data, ...prevChats];
        });
      } else {
        // If it doesn't exist, we should probably fetch queries or at least ensure data is populated.
        // For now, since we fixed backend to populate, this is okay.
        fetchQueries(chatStatus, searchText, 1, false);
      }
    };

    const handleReceiveReply = (data) => {
      const isCurrent = data.queryid === queryIdRef.current;

      if (isCurrent) {
        setMessages((prev) => {
          const existingIndex = prev.findIndex((m) =>
            (data._id && String(m._id) === String(data._id)) ||
            (data.tempId && String(m.tempId || m._id) === String(data.tempId))
          );

          if (existingIndex !== -1) {
            const newMessages = [...prev];
            newMessages[existingIndex] = {
              ...data,
              seen: data.seen,
              createdAt: data.createdAt || new Date()
            };
            return newMessages;
          }

          return [...prev, {
            ...data,
            seen: data.role === "admin" ? data.seen : true,
            createdAt: data.createdAt || new Date()
          }];
        });
      }

      setChats(prevChats => prevChats?.map((chat) => {
        if (chat._id === data.queryid) {
          if (isCurrent && socket && data.role === "user") {
            socket.emit("mark_messages_read", {
              queryid: data.queryid,
              role: "admin"
            });
          }

          const newAnswer = [...(chat.Answer || [])];
          const existingInAnswer = newAnswer.findIndex(a =>
            (data._id && String(a._id) === String(data._id)) ||
            (data.tempId && String(a.tempId) === String(data.tempId))
          );
          if (existingInAnswer !== -1) {
            newAnswer[existingInAnswer] = { ...data, seen: data.seen };
          } else {
            newAnswer.push({ ...data, seen: data.seen });
          }

          return {
            ...chat,
            Answer: newAnswer,
            unreadCount: isCurrent ? 0 : ((chat.unreadCount || 0) + (data.role !== "admin" ? 1 : 0)),
          };
        }
        return chat;
      })
      );
    };

    const handleReceiveCloseTicket = (data) => {
      const closedId = data._id || data.id;
      if (closedId === queryIdRef.current) {
        setIsDisabled(true);
      }
      setChats((prevChats) =>
        prevChats.map((chat) => {
          if (chat._id === closedId) {
            return { ...chat, ...data, solved: true, isDisabled: true };
          }
          return chat;
        })
      );
    };

    const handleMessagesRead = ({ queryid }) => {
      if (queryid === queryIdRef.current) {
        setMessages(prev =>
          prev.map(msg =>
            msg.role === "admin"
              ? { ...msg, seen: true }
              : msg
          )
        );
      }
    };

    const handleUserJoined = ({ queryid }) => {
      if (queryid === queryIdRef.current) {
        setMessages(prev =>
          prev.map(msg =>
            msg.role === "admin"
              ? { ...msg, seen: true }
              : msg
          )
        );
      }
    };

    socket.on("receive_raised_ticket", handleReceiveRaisedTicket);
    socket.on("receive_reply", handleReceiveReply);
    socket.on("receive_close_ticket", handleReceiveCloseTicket);
    socket.on("messages_read", handleMessagesRead);
    socket.on("user_joined_room", handleUserJoined);

    return () => {
      socket.off("receive_raised_ticket", handleReceiveRaisedTicket);
      socket.off("receive_reply", handleReceiveReply);
      socket.off("receive_close_ticket", handleReceiveCloseTicket);
      socket.off("messages_read", handleMessagesRead);
      socket.off("user_joined_room", handleUserJoined);
    };
  }, [socket, chatStatus, searchText]);
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 900;
      setIsMobile(mobile);
      if (!mobile) setShowSidebar(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchSingleQuery = async (id) => {
    try {
      const response = await axios.post(`${consts.BackendUrl}/support/getallquery`, {
        queryid: id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response?.data?.success) {
        const selected = response.data.result;
        setMessages(selected?.Answer || []);
        setTitle(selected?.title);
        setIsDisabled(selected?.solved || false);

        setTimeout(() => scrollToBottom("auto"), 50);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectChat = (id) => {
    if (queryId === id) return;

    setQueryId(id);
    const selected = chats.find((c) => c._id === id);

    if (selected) {
      setChats(prev => prev.map(c => c._id === id ? { ...c, unreadCount: 0 } : c));
      setMessages(selected.Answer || []);
      setTitle(selected.title);
      setIsDisabled(selected.solved || selected.isDisabled || false);

      if (socket) {
        socket.emit("mark_messages_read", {
          queryid: id,
          role: "admin"
        });
      }

      if (!selected.Answer || selected.Answer.length === 0) {
        fetchSingleQuery(id);
      } else {
        setTimeout(() => scrollToBottom("auto"), 50);
      }
    }

    if (isMobile) setShowSidebar(false);
  };

  const handleSend = async () => {
    if (!messageInput.trim() || isDisabled) return;

    const tempId = Date.now();
    const tempMsg = {
      _id: tempId,
      tempId: tempId,
      reply: messageInput,
      role: "admin",
      createdAt: new Date(),
      seen: false
    };

    setMessages(prev => [...prev, tempMsg]);

    const payload = {
      queryId: queryId,
      reply: messageInput
    };

    setMessageInput("");

    try {
      socket.emit("send_reply", {
        queryId: queryId,
        reply: messageInput,
        role: "admin",
        token: token,
        tempId: tempId
      });
    } catch (err) {
      message.error("Error sending message");
    }
  };

  const scrollToBottom = (behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (messages.length > 0) {
      scrollToBottom("smooth");
    }
  }, [messages]);

  const formatTime = (date) =>
    new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  const handleDisableChat = async (reason) => {
    try {
      const response = await axios.post(`${consts.BackendUrl}/support/adminclosequery`, {
        id: queryId,
        reason: reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response?.data?.success) {
        setIsDisabled(true);
        setIsModalOpen(false);
        form.resetFields();
        fetchQueries(chatStatus, searchText, 1, false);
      } else {
        message.error("Failed to close ticket");
      }
    } catch (err) {
      message.error("Error closing ticket");
    }
  };

  const menu = (
    <Menu
      items={[
        {
          key: "1",
          label: "Close Ticket",
          onClick: () => setIsModalOpen(true),
        },
      ]}
    />
  );

  return (
    <>
      <Loadingpage  isLoading={loadingMore}/>

      <div className="app-container">
        {showSidebar && (
          <div className="sidebar ar3 sidebar-bg-clr" style={{ width: isMobile ? " 100%" : "320px" }}>
            <div className="display-5 support_sidebar_header">
              <span>Chat</span>
              <Select
                value={chatStatus}
                onChange={(val) => {
                  setChatStatus(val);
                  setPage(1);
                  setQueryId("");
                  setMessages([]);
                  setTitle("");
                  setIsDisabled(false);
                }}
              >
                <Select.Option value="opened">Opened</Select.Option>
                <Select.Option value="closed">Closed</Select.Option>
              </Select>
            </div>
            <Search
              placeholder="Search..."
              style={{ marginBottom: 10 }}
              onChange={(e) => {
                const value = e.target.value;

                setSearchText(value);

                // clear icon click
                if (!value) {
                  fetchQueries(chatStatus, "", 1, false);
                }
              }}
              onSearch={(value) => {
                fetchQueries(chatStatus, value, 1, false);
              }}
              allowClear
              className="input-cls"

            />

            <div className="history-container" onScroll={handleScroll}>
              <h3>Chat History</h3>
              {chats.map((chat) => (
                <div
                  key={chat._id}
                  className={`chat-history-item ${queryId === chat._id ? "active" : ""
                    }`}
                  onClick={() => handleSelectChat(chat._id)}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <UserOutlined />

                  <div style={{ flex: 1, marginLeft: 10 }}>
                    <div style={{ fontWeight: "bold" }}>
                      {chat.user_id?.name ||
                        chat.user_id?.email ||
                        "Unknown User"}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#888",
                          width: "100px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {chat.title}
                      </div>
                      <div style={{
                        marginTop: "-20px", display: "flex", flexDirection: "column",
                        gap: "5px", alignItems: "center",
                      }}>
                        <div style={{ position: "relative", marginRight: 10 }}>
                          <Badge
                            count={chat.unreadCount !== undefined ? chat.unreadCount : (chat.Answer?.filter(a => !a.seen && a.role === 'user').length || 0)}
                            style={{
                              backgroundColor: "#ff4d4f",
                              width: 18,
                              height: 18,
                              borderRadius: "60%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 8,
                            }}
                          />
                        </div>
                        <small>
                          {chat.Answer && chat.Answer.length > 0
                            ? formatTime(
                              chat.Answer[chat.Answer.length - 1]?.createdAt,
                            )
                            : "No messages"}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* <MoreOutlined /> */}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CHAT AREA */}
        {(!isMobile || !showSidebar) && (
          <div className="chat-container">
            {/* HEADER */}
            <div className="chat-header">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "14px",
                  color: "#fff",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
              >
                {isMobile && queryId && (
                  <ArrowLeftOutlined
                    style={{
                      marginRight: 10,
                      cursor: "pointer",
                      color: "#fff",
                    }}
                    onClick={() => {
                      setShowSidebar(true);
                      setQueryId("");
                    }}
                  />
                )}
                <div>{title || "Select Chat"}</div>
              </div>

              {queryId && (
                <Dropdown overlay={menu} trigger={["click"]}>
                  {!isDisabled && (
                    <MoreOutlined
                      style={{
                        cursor: "pointer",
                        color: "#fff",
                      }}
                    />
                  )}
                </Dropdown>
              )}
            </div>

            {/* MESSAGES */}
            <div
              className="messages"
              style={
                !queryId
                  ? {
                    display: "flex",
                    alignItems: "center",
                    color: "#fff",
                    justifyContent: "center",
                    flexDirection: "column",
                    height: "100%",
                    opacity: 0.5,
                  }
                  : {}
              }
            >
              {!queryId ? (
                <div style={{ textAlign: "center" }}>
                  <MessageOutlined
                    style={{ fontSize: "48px", marginBottom: "10px" }}
                  />
                  <div style={{ fontSize: "18px" }}>
                    Select a chat to start messaging
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: "center", marginTop: "20px" }}>
                  No messages yet.
                </div>
              ) : (
                messages.map((msg, index) => (
                  <div
                    key={msg._id}
                    className={`message ${msg.role === "admin" ? "message-right" : "message-left"
                      }`}
                  >
                    <div className="bubble">
                      <div>{msg.reply}</div>

                      <div className="msg-time">
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <span>{formatTime(msg.createdAt)}</span>

                          {msg.role === "admin" && (
                            <span className="msg-tick">
                              {msg.seen ? (
                                <span className="msg-dudble-tick">
                                  <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 26 26"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M3 12L7 16L13 8"
                                      stroke={msg.seen ? "blue" : "black"}
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M10 12L14 16L21 6"
                                      stroke={msg.seen ? "blue" : "black"}
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </span>
                              ) : (
                                <svg
                                  width="15"
                                  height="15"
                                  viewBox="0 0 26 26"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M3 12L7 16L13 8"
                                    stroke="#000000"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* DISABLED INFO */}
            {isDisabled && (
              <div style={{ textAlign: "center", color: "red", padding: 5 }}>
                Ticket is closed
              </div>
            )}
            {/* INPUT (always visible but disabled) */}
            {messages.length > 0 && (
              <div
                className="input-area"
                style={
                  isDisabled
                    ? {
                      filter: "blur(1px)",
                      pointerEvents: "none",
                      opacity: 0.9,
                    }
                    : {}
                }
              >
                <TextArea
                  placeholder="Type message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  autoSize={{ minRows: 1, maxRows: 5 }}
                  disabled={isDisabled}
                />

                <button
                  onClick={handleSend}
                  className="send-btn"
                  disabled={isDisabled}
                >
                  <SendOutlined />
                </button>
              </div>
            )}

            {/* MODAL */}
            <Modal
              title="Close Ticket"
              open={isModalOpen}
              onCancel={() => setIsModalOpen(false)}
              footer={null}
              centered
              className="can-ticket"
              wrapClassName="can-ticket-wrap"
              maskStyle={{
                backdropFilter: "blur(8px)",
                // backgroundColor: "rgba(0, 0, 0, 0.2)",
              }}
              styles={{
                content: {
                  background: "linear-gradient(180deg, rgba(252, 68, 32, 0.2) 0 %, rgba(252, 68, 32, 0.04) 100 %)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid #FC442080",
                },
                header: {
                  backgroundColor: "transparent",
                },
              }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={(values) => {
                  handleDisableChat(values.reason);
                }}
              >
                <Form.Item
                  label="Reason"
                  name="reason"
                  rules={[{ required: true, message: "Please enter reason" }]}
                >
                  <Input.TextArea
                    placeholder="Enter reason..."
                    maxLength={100}
                  />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" block>
                    Submit
                  </Button>
                </Form.Item>
              </Form>
            </Modal>
          </div>
        )}
      </div>
    </>
  );
};

export default Supports;
